import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { provideBlotter, DEFAULT_ROW_HEIGHT } from "../bfx-base-blotter/bfx-base-blotter";
import {
  ApplicationRef,
  ElementRef,
  Injector,
  NgZone,
  ViewChild,
  ViewEncapsulation,
  Component,
} from "@angular/core";

import { GoldenLayoutService } from "../golden-layout/golden-layout-service";

import { TranslateService } from "@ngx-translate/core";
import { BfxBlotterFilterService } from "../bfx-blotter-filter/bfx-blotter-filter.service";

import {
  ProfilePromiseActionService,
  ProfileSettingKeys,
  UserTemperatureService,
} from "shared/cubiq-services";
import {
  GridOptions,
  RowNode,
  CellClickedEvent,
  SortChangedEvent,
  ICellRendererParams,
  CellContextMenuEvent,
} from "ag-grid-community/main";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { Temperature } from "shared/cubiq-services";
import { BfxUserProfileService } from "../bfx-common/services/bfx-user-profile.service";
import { ActiveUserProfilePromiseService } from "shared/auth-services";

import {
  COLUMN_ORDER_DEALT_TRACKING,
  COLUMN_INSTRUMENT,
  COLUMN_LADDER_POSITION,
} from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";

import { BfxUserSettingsService } from "../bfx-user-settings/bfx-user-settings.service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

const LADDER_SORT: Array<{ colId: string; sort: string }> = [
  { colId: COLUMN_INSTRUMENT, sort: "asc" },
  { colId: COLUMN_ORDER_DEALT_TRACKING, sort: "desc" },
];

import { BfxSaveProfileNotificationService } from "../bfx-common/services/bfx-save-profile-notification.service";
import { OrderLadderDefinitionFactory } from "../../shared/cubiq-services/model/OrderLadderDefinitionFactory";
import { BfxBlotterOrders } from "../bfx-blotter-orders/bfx-blotter-orders";
import { SystemConfigPromiseService } from "../../shared/cubiq-services/index";
import { BfxOrderLadderFilteringService } from "./services/bfx-ladder-orders-filter.service";
import { BfxMouseModifierKeyService } from "../bfx-common/services/bfx-mouse-modifier-key.service";
import { BfxLadderOrdersService } from "./services/bfx-ladder-orders.service";
import { LadderPair } from "../../shared/cubiq-services/model/orders/LadderPair";
import { LadderPairRenderer } from "./ladder-pair-renderer";

import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { OrderPosition } from "../../shared/cubiq-services/model/orders/OrderPosition";
import { COLUMN_SELECTION } from "shared/cubiq-services/model/TableDefinitionFactory.constants";
import { OrderTypeRenderer } from "../bfx-blotter-orders/renderers/OrderTypeRenderer";

import { SpotTrackingRenderer } from "../bfx-blotter-orders/renderers/SpotTrackingRenderer";
import { MarketMidRenderer } from "../bfx-blotter-orders/renderers/MarketMidRenderer";
import { FwdPtsRenderer } from "../bfx-blotter-orders/renderers/FwdPtsRenderer";
import { PercentAwayRenderer } from "../bfx-blotter-orders/renderers/PercentAwayRenderer";
import { LimitOrder } from "shared/cubiq-services/model/orders/LimitOrder";
import { LadderRate } from "../../shared/cubiq-services/model/orders/LadderRate";
import { FillRenderer } from "../bfx-blotter-orders/renderers/FillRenderer";
import { BfxLadderOrdersContextMenuService } from "./bfx-ladder-orders-contextmenu.service";
import { FillPercentRenderer } from "app/bfx-blotter-orders/renderers/FillPercentRenderer";
import { BfxVwapInfo } from "app/bfx-vwap/bfx-vwap-info";
import { BfxVwapOrdersService } from "app/bfx-vwap/bfx-vwap-orders.service";
import { OrdersTableMode } from "../../shared/cubiq-services/model/OrdersTableMode";
import { BfxColDef } from "../../shared/cubiq-services/model/TableDefinition";
import {
  RowDataTransaction,
  RowNodeTransaction,
} from "ag-grid-community/dist/lib/rowModels/clientSide/clientSideRowModel";
import { LadderAggregate } from "../../shared/cubiq-services/model/orders/LadderAggregate";
import { NullProtoObject } from "../../shared/helper-services/null-proto-object";
import { auditTime, Subject } from "rxjs";
import { CommonIntentRenderer } from "app/bfx-blotter-orders/renderers/CommonIntentRenderer";

export const PAIR_DIVIDER_HEIGHT: number = 2;
const BATCH_UPDATE_WAIT_MS = 200;

@Component({
  selector: "bfx-ladder-orders",
  templateUrl: "./html/bfx-ladder-orders.html",
  encapsulation: ViewEncapsulation.None,
  providers: [provideBlotter(BfxLadderOrders), BfxLadderOrdersService],
  standalone: false,
})
export class BfxLadderOrders extends BfxBlotterOrders {
  @ViewChild("tableTarget", { static: true }) blotterView: ElementRef;

  protected filteredOrderIds: Set<string> = new Set();
  protected filteredOrdersPositions: Map<string, OrderPosition> = new Map();

  public readonly isLadderOrders = true;

  public showFullTray: boolean = true; // will reuse this when we introduce the show hide pair tray, no longer using the minimised view

  public currencyPairTooltip: string | number;

  private accumulateBatchSubscription;
  private doBatchUpdateSubscription;
  private doBatchUpdateSubject = new Subject<void>();

  private pendingBatchTransaction: {
    add: Map<string, object>;
    update: Map<string, object>;
    remove: Map<string, object>;
    callbacks: Set<(res: RowNodeTransaction) => void>;
  } = {
    add: new Map(),
    update: new Map(),
    remove: new Map(),
    callbacks: new Set(),
  };
  private parentFiltered: NullProtoObject<boolean> = Object.create(null);

  checkQueueTimeoutId: NodeJS.Timeout;
  aggregateExpanded: Set<string> = new Set();
  private _aggregateRowDeselectInProgress: boolean;
  private _aggregateRowDeselectInProgressFrame;
  private processingModelUpdateEvent: boolean;
  private processingPostSortEvent: boolean;

  public get aggregateRowDeselectInProgress(): boolean {
    return this._aggregateRowDeselectInProgress;
  }

  public set aggregateRowDeselectInProgress(value: boolean) {
    if (!value) {
      this._aggregateRowDeselectInProgressFrame = requestAnimationFrame(() => {
        this._aggregateRowDeselectInProgressFrame = null;
        this._aggregateRowDeselectInProgress = false;
      });
      return;
    }

    if (this._aggregateRowDeselectInProgressFrame !== null) {
      cancelAnimationFrame(this._aggregateRowDeselectInProgressFrame);
      this._aggregateRowDeselectInProgressFrame = null;
    }

    this._aggregateRowDeselectInProgress = value;
  }

  constructor(
    BfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public service: BfxLadderOrdersService,
    _contextMenuService: BfxLadderOrdersContextMenuService,
    zone: NgZone,
    // see _tableDefinitionFactory
    factory: OrderLadderDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    filteringService: BfxOrderLadderFilteringService,
    activeUserService: ActiveUserProfilePromiseService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    sysConfigService: SystemConfigPromiseService,
    userSettingsService: BfxUserSettingsService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    protected userProfileService: BfxUserProfileService,
    protected userTempService: UserTemperatureService,
    protected vwapOrdersService: BfxVwapOrdersService,
    protected vwapInfo: BfxVwapInfo,
  ) {
    super(
      BfxBlotterElement,
      applicationRef,
      injector,
      service,
      _contextMenuService,
      zone,
      factory,
      layoutService,
      translationService,
      blotterFilterService,
      filteringService,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      userSettingsService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
      userTempService,
      vwapOrdersService,
      vwapInfo,
    );

    this.profileActionService
      .getProfileConfig(ProfileSettingKeys.userProfileSettingsLadderOrdersPreferences)
      .then((profileValue) => {
        this.mapLadderSettings(profileValue);
      });

    this.profileActionService
      .getProfileConfigUpdates(ProfileSettingKeys.userProfileSettingsLadderOrdersPreferences)
      .subscribe((profileValue) => {
        this.mapLadderSettings(profileValue);
      });

    NativeDesktopSupport.prepareGridOptions(this.gridOptions, BfxBlotterElement.nativeElement);
  }

  getVwapSource(): string {
    return "Order Ladder";
  }

  public initializeInMemory() {
    this.gridOptions.batchUpdateWaitMillis = BATCH_UPDATE_WAIT_MS;

    this.service.isReady().then(() => {
      let rowData: Array<any> = this.service.getRowData();

      for (let data of rowData) {
        if (data.isLadderRate || data.fullWidth) {
          continue;
        }
        this.updateTemperature(data);
      }

      this.gridOptions.api.setRowData(rowData);
      this.onAdaptedDataPrimed();
      this.blotterReady();
    });

    this.gridOptions.isExternalFilterPresent = this.isExternalFilterPresent.bind(this);
    this.gridOptions.doesExternalFilterPass = this.doesExternalFilterPass.bind(this);
    this.defaultSortingModel = this._tableDefinitionFactory.getDefaultSortingModel();

    this.service.invertPair$.subscribe(this.onInvertPairEvent.bind(this));

    if (!this.service.hasBatchUpdateSubscription) {
      this.accumulateBatchSubscription = this.service.batchTransactionSubject.subscribe((value) => {
        this.accumulateBatchUpdate(value);
        this.doBatchUpdateSubject.next();
      });
      this.service.hasBatchUpdateSubscription = true;

      this.doBatchUpdateSubscription = this.doBatchUpdateSubject
        .pipe(auditTime(BATCH_UPDATE_WAIT_MS))
        .subscribe(() => this.doBatchUpdate());
    }
  }

  public frameworkComponents: any = {
    ladderPairRenderer: LadderPairRenderer,
    orderTypeRenderer: OrderTypeRenderer,
    intentRenderer: CommonIntentRenderer,
    fillRenderer: FillRenderer,
    fillPercRenderer: FillPercentRenderer,
    spotTrackingRenderer: SpotTrackingRenderer,
    percentAwayRenderer: PercentAwayRenderer,
    marketMidRenderer: MarketMidRenderer,
    fwdPtsRenderer: FwdPtsRenderer,
  };

  protected changeLadderConfig(jsonData): void {
    let data = jsonData != null && typeof jsonData === "string" ? JSON.parse(jsonData) : null;
    let changed = false;

    if (data == null || typeof data !== "object") return;

    if (changed) {
      if (this.gridOptions && this.gridOptions.api) {
        this.gridOptions.api.redrawRows();
        this.gridOptions.api.onFilterChanged();
      }
    }
  }

  protected createsGridOptions(): GridOptions {
    let gridOptions = super.createsGridOptions();
    gridOptions.isRowSelectable = (rowNode) => {
      return !rowNode.data.fullWidth && !rowNode.data.isLadderRate;
    };

    gridOptions.getRowHeight = function (params) {
      // market bar
      if (params.node.data.constructor.name == LadderRate.name) {
        // Ladde rrate
        return DEFAULT_ROW_HEIGHT;
      } else if (params.node.data.fullWidth) {
        return PAIR_DIVIDER_HEIGHT;
      } else {
        // normal order
        return DEFAULT_ROW_HEIGHT;
      }
    };

    gridOptions.postSort = function postSort(this: BfxLadderOrders, nodes: Array<RowNode>) {
      if (this.processingPostSortEvent) {
        return;
      }

      const positionColumnDef = this.gridOptions?.columnApi?.getColumn(COLUMN_LADDER_POSITION);
      const positionVisibility: boolean = positionColumnDef?.["visible"] === true;

      if (this.filteredOrdersPositions) this.filteredOrdersPositions.clear();

      if (!positionVisibility) return;

      this.processingPostSortEvent = true;

      if (nodes) {
        nodes.forEach((rowNode) => {
          if (rowNode.data.getPosition) {
            let { amt, ccy } = rowNode.data.getPosition(gridOptions.context);
            let orderId: string = rowNode.data.orderId.toString();
            let currency: CubiqTS.CurrencyImpl = this.service.currencyBaseService.getCcy(ccy);

            this.filteredOrdersPositions.set(
              orderId,
              new OrderPosition(
                orderId,
                amt,
                rowNode.data[COLUMN_ORDER_DEALT_TRACKING] > 0,
                currency.positionUnitId,
              ),
            );
          }
        });
      }
      this.service.updateCurrentFilteredPositions(this.filteredOrdersPositions, () => {
        this.processingPostSortEvent = false;
      });
    }.bind(this);

    gridOptions.isExternalFilterPresent = this.isExternalFilterPresent.bind(this);
    gridOptions.doesExternalFilterPass = this.doesExternalFilterPass.bind(this);
    gridOptions.onSortChanged = this.onSortChanged.bind(this);

    gridOptions.context.isInverted = (order: any) => {
      return this.service.isPairInverted(order.baseCode + "/" + order.nonBaseCode);
    };

    gridOptions.context.getLadderAggregate = (order: any) => {
      return this.service.getRawDataObject()[order.aggregationId];
    };

    gridOptions.context.getLimitOrder = (orderId: string) => {
      return this.service.getRawDataObject()[orderId];
    };

    gridOptions.context.redrawLimitOrder = (orderId: string) => {
      setTimeout(() => {
        gridOptions.api.redrawRows({ rowNodes: [gridOptions.api.getRowNode(orderId)] });
      }, 0);
    };

    gridOptions.context.isAggregateExpanded = (aggregateId: string) => {
      if (
        this.mode !== OrdersTableMode.BASE &&
        this.service.getRawDataObject()?.[aggregateId]?.product === CubiqTS.Product.NDF
      ) {
        return false;
      }

      return this.aggregateExpanded.has(aggregateId);
    };

    gridOptions.context.supportsAggregation = () => this.supportsAggregation;

    gridOptions.context.clickAggregate = ($event: PointerEvent, params: ICellRendererParams) => {
      if (
        LadderAggregate.is(params.data) &&
        $event.target instanceof Element &&
        $event.target.classList.contains("ladder-orders-aggregate-expansion-icon")
      ) {
        let aggregateId = params.data.orderId;
        if (this.aggregateExpanded.has(aggregateId)) {
          this.aggregateExpanded.delete(aggregateId);
          params.data.aggregateExpanded = false;
        } else {
          this.aggregateExpanded.add(aggregateId);
          params.data.aggregateExpanded = true;
        }

        const update = [
          params.data,
          ...[...params.data.aggregateOrderIdSet].map((id) => {
            const limitOrder: LimitOrder = this.gridOptions.api.getRowNode(id).data;
            limitOrder.isAggregated = true;
            return limitOrder;
          }),
        ];

        this.gridOptions.api.updateRowData({ update: update });
        this.gridOptions.api.onFilterChanged();
        if (params.node.isSelected()) {
          this.onRowSelected({ node: params.node });
          this.onSelectionChanged();
        }
      }
    };

    return gridOptions;
  }

  private onSortChanged(_: SortChangedEvent): void {
    let curSort = this.gridOptions.api.getSortModel();
    if (!curSort || curSort.length != 2 || curSort[0].colId != LADDER_SORT[0].colId) {
      this.gridOptions.api.setSortModel(LADDER_SORT);
    }
  }

  protected getConfigToSaveAsDefault(): any {
    let defColAndSort = super.getConfigToSaveAsDefault();
    defColAndSort["showFullTray"] = this.showFullTray;
    return defColAndSort;
  }

  public restoreFromConfiguration(config: any): void {
    super.restoreFromConfiguration(config);
    if (config != null && config.showFullTray != null) {
      this.showFullTray = config.showFullTray;
    }
    if (config != null && config.pairIsInvertArray != null) {
      this.service.restorePairIsInvertValues(config.pairIsInvertArray);
    }
  }

  public getComponentConfiguration(): any {
    let configuration: any = super.getComponentConfiguration();
    configuration.showFullTray = this.showFullTray;
    configuration.pairIsInvertArray = this.service.getPairIsInvertValues();
    return configuration;
  }

  public getBlotterTypeStr(): string {
    let type: string = super.getBlotterTypeStr();
    return type + "_LADDER";
  }

  public getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.ORDER;
  }

  protected checkIfNeedMarketBar(): void {}

  protected selectedRowsLength(): number {
    let count: number = 0;

    if (this.gridOptions.api) {
      let rows: any = this.gridOptions.api.getSelectedRows();
      count = rows.length;
      for (let i: number = 0; i < rows.length; i++) {
        if (
          rows[i] instanceof LadderRate ||
          rows[i] instanceof LadderPair ||
          (LadderAggregate.is(rows[i]) && this.aggregateExpanded.has(rows[i].aggregateId))
        ) {
          count -= 1;
        } else if (
          LadderAggregate.is(rows[i]) &&
          !this.aggregateExpanded.has(rows[i].aggregateId)
        ) {
          count += rows[i].aggregateOrderIdSet.size - 1;
        }
      }
    }

    return count;
  }

  public showSettings(event: Event): void {
    this.layoutService.showSettings("OrderLadder", event);
  }

  public isExternalFilterVisible(node: RowNode): boolean {
    return super.checkExternalFilterVisibility(node);
  }

  protected checkExternalFilterVisibility(node: RowNode): boolean {
    if (node.data.pairIsEmpty === true) {
      return false;
    } else if (node.data.pairIsEmpty === false) {
      return true;
    } else if (LadderAggregate.is(node.data)) {
      if (!this.supportsAggregation) {
        return false;
      } else if (node.data.product === CubiqTS.Product.NDF && this.mode !== OrdersTableMode.BASE) {
        return false;
      }
      let rows: Array<RowNode> = [...node.data.aggregateOrderIdSet].map((id: string) =>
        this.gridOptions.api.getRowNode(id),
      );
      let visibleRows: Array<RowNode> = rows.filter((row: RowNode) =>
        super.checkExternalFilterVisibility(row),
      );
      let aggregateVisibleCount = (node.data.aggregateVisibleCount = visibleRows.length);
      let wasFiltered = this.parentFiltered[node.data.aggregateId] ?? false;
      let filtered = (this.parentFiltered[node.data.aggregateId] = aggregateVisibleCount < 2);

      if (wasFiltered !== filtered) {
        this.gridOptions.api.refreshCells({
          rowNodes: rows,
          columns: [COLUMN_SELECTION],
          force: true,
        });
      }

      return !filtered;
    } else if (this.supportsAggregation && node.data?.isAggregated) {
      if (!super.checkExternalFilterVisibility(node)) {
        node.data.aggregate.resetAggregateValues();
        return false;
      } else if (node.data.product === CubiqTS.Product.NDF && this.mode !== OrdersTableMode.BASE) {
        return true;
      }

      return this.aggregateExpanded.has(node.data?.aggregateId);
    }

    return super.checkExternalFilterVisibility(node);
  }

  protected searchComplete(): void {
    if (this.gridOptions.api) {
      let refreshRows: Array<RowNode> = [];
      this.gridOptions.api.forEachNodeAfterFilterAndSort((rowNode: RowNode) => {
        refreshRows.push(rowNode);
      });

      if (
        this.gridOptions &&
        this.gridOptions.api &&
        Array.isArray(refreshRows) &&
        refreshRows.length > 0
      ) {
        this.gridOptions.api.refreshCells({
          rowNodes: refreshRows,
          columns: [this.gridOptions.columnApi.getAllColumns()[0]],
          force: true,
        });
      }
    }
  }

  protected onModelUpdated(): void {
    if (this.processingModelUpdateEvent) {
      return;
    }

    this.processingModelUpdateEvent = true;
    let filteredOrderIds = null;

    if (
      Temperature.Cold != this.temperatureSetting ||
      this.isExternalFilterPresent() ||
      (this.searchValue != undefined && this.searchValue != "")
    ) {
      this.filteredOrderIds.clear();
      this.gridOptions?.api?.forEachNodeAfterFilter((rowNode: RowNode) => {
        if (!rowNode.data.fullWidth && !rowNode.data.isLadderRate) {
          this.filteredOrderIds.add(rowNode.data.orderId);
        }
      });
      filteredOrderIds = this.filteredOrderIds;
    } else if (this.filteredOrderIds.size > 0) {
      this.filteredOrderIds.clear();
    }

    this.service.updateCurrentFilteredOrderIds(filteredOrderIds, () => {
      this.gridOptions?.api?.refreshCells({
        columns: [COLUMN_SELECTION],
        force: true,
      });

      this.processingModelUpdateEvent = false;
    });
  }

  public onCellDoubleClicked(event) {
    if (
      event == Object(event) &&
      event.node === Object(event.node) &&
      !event.node.data.isLadderRate &&
      !event.node.data.isLadderAggregate
    ) {
      super.onCellDoubleClicked(event);
    }
  }

  public onContextMenuSelectRow(event: CellContextMenuEvent) {
    super.onContextMenuSelectRow(event);
    if (LadderAggregate.is(event.data) && this.aggregateExpanded.has(event.data.aggregateId)) {
      this._aggregateRowDeselectInProgress = true;
      this.selectAllAggregatedRow(event, [event.node]);
      this._aggregateRowDeselectInProgress = false;
    }
  }

  public onPairClicked(pair: LadderPair): void {
    this.gridOptions.api.ensureNodeVisible(pair.associatedRate, "middle");
  }

  public onPairHovered(pair: LadderPair): void {
    let orderCount = pair.currentOrderCount;
    let instrument = pair.selection;
    this.currencyPairTooltip =
      orderCount > 0 && orderCount != 1
        ? `${instrument}: ${orderCount} Orders`
        : `${instrument}: ${orderCount} Order`;
  }

  public toggleCcyPairTray(): void {
    this.showFullTray = !this.showFullTray;
    this.blotterView.nativeElement.style.height = !this.showFullTray ? "100%" : "";
  }

  protected applyColumnDefinition(column): any {
    let col = super.applyColumnDefinition(column);
    if (column.index == 0) {
      col.cellRenderer = "ladderPairRenderer";
      col.checkboxSelection = (params) => {
        return !(params.data instanceof LadderRate || params.data instanceof LadderPair);
      };

      col.onCellClicked = (params: CellClickedEvent) => {
        if (params.data.constructor.name == LadderRate.name) {
          let ladderRate: LadderRate = params.data;
          this.invertPair(ladderRate.baseCode + "/" + ladderRate.nonBaseCode + "_div");
        }
      };
    }
    return col;
  }

  public invertPair(pair): void {
    this.service.invertPair(pair);
  }

  protected onInvertPairEvent(rows: Array<any>): void {
    if (this.tableDefinition && this.gridOptions && this.gridOptions.api) {
      if (!this.areUpdatesFrozen()) {
        this.gridOptions.api.updateRowData({
          update: rows,
        });
        this.onSelectionChanged();
      }
    }
  }

  public isExternalFilterPresent(): boolean {
    return true;
  }

  protected afterFilteringCompleted(): void {
    super.afterFilteringCompleted();
  }

  public hasCellRenderedSelection(): boolean {
    return true;
  }

  protected checkPrimaryHotStyle(params): boolean {
    return Temperature.Hot == params.data.ordTemp;
  }
  protected checkPrimaryWarmStyle(params): boolean {
    return Temperature.Warm == params.data.ordTemp;
  }
  protected checkPrimaryColdStyle(params): boolean {
    return Temperature.Cold == params.data.ordTemp;
  }

  protected checkHotStyle(params): boolean {
    if (this.isFullTempStyle && this.isTempOnRow(params)) {
      return Temperature.Hot == params.data.ordTemp;
    }
    return false;
  }
  protected checkWarmStyle(params): boolean {
    if (this.isFullTempStyle && this.isTempOnRow(params)) {
      return Temperature.Warm == params.data.ordTemp;
    }
  }
  protected checkColdStyle(params): boolean {
    if (this.isFullTempStyle && this.isTempOnRow(params)) {
      return Temperature.Cold == params.data.ordTemp;
    }
  }
  protected isTempOnRow(params) {
    if (params.data != null && params.data.isLadderRate === true) {
      // ladder row never takes temp on row style
      return false;
    }

    return super.isTempOnRow(params);
  }

  public doesTemperatureFilterPass(blotterTemperature: Temperature, rawData: LimitOrder): boolean {
    if (rawData != null && rawData.isLadderRate === true) {
      // here type of rawData can be LadderRate but just check property to skip
      //  calc & side effects
      return true;
    }

    return super.doesTemperatureFilterPass(blotterTemperature, rawData);
  }

  /**
   * Returns the row items currently selected aside from the ones
   * that have IDs filtered out by the isValidRowID method
   */
  protected getSelectedRows(): Array<any> {
    let selectedRows = this.gridOptions.api.getSelectedRows();
    let validatedRows = new Map<string, any>();

    if (selectedRows != null && selectedRows.length > 0) {
      selectedRows.forEach((row: any) => {
        if (LadderAggregate.is(row) && !this.aggregateExpanded.has(row.aggregateId)) {
          row.aggregateOrderIdSet.forEach((id) => {
            let aggregatedRow = this.service.getRawDataObject()?.[id];
            if (
              aggregatedRow[this.tableDefinition.primaryKey] != null &&
              this.isValidRowID(aggregatedRow[this.tableDefinition.primaryKey])
            ) {
              validatedRows.set(aggregatedRow[this.tableDefinition.primaryKey], aggregatedRow);
            }
          });
        } else if (
          row[this.tableDefinition.primaryKey] != null &&
          this.isValidRowID(row[this.tableDefinition.primaryKey])
        ) {
          validatedRows.set(row[this.tableDefinition.primaryKey], row);
        }
      });
    }

    return Array.from(validatedRows.values());
  }

  protected onFullUpdateEvent(rows: Array<any>): void {
    // no-op see onBatchUpdate()
  }
  protected onPartialUpdateEvent(rows: Array<any>): void {
    // no-op see onBatchUpdate()
  }

  protected onDeleteEvent(rowId: string): void {
    // no-op see onBatchUpdate()
  }

  protected checkQueue() {
    // no-op see onBatchUpdate()
  }

  protected processedRateUpdate(transaction: any): void {
    // no-op see onBatchUpdate()
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();

    clearTimeout(this.checkQueueTimeoutId);

    if (this.accumulateBatchSubscription != null) {
      this.accumulateBatchSubscription.unsubscribe();
    }

    if (this.doBatchUpdateSubscription != null) {
      this.doBatchUpdateSubscription.unsubscribe();
    }
  }

  private mapLadderSettings(profileValue: any): void {
    try {
      let savedSettings = JSON.parse(profileValue);
      if (savedSettings != undefined && savedSettings != null) {
        let supportsAggregation = savedSettings?.supportsAggregation ?? false;
        if (supportsAggregation !== this.supportsAggregation) {
          this.supportsAggregation = supportsAggregation;
          this.aggregateExpanded.clear();
        }
      }
    } catch (e) {}
  }

  protected postUserSettingsRefresh() {
    // no-op - do not trigger super / partial update
  }

  public getTabIdentifier(): string {
    return "BfxLadderOrders";
  }

  protected updateSearch() {
    if (this.searchValue != null && this.searchValue != "" && this.supportsAggregation) {
      this.quickFilterAggregation = true;
      this.supportsAggregation = false;
      this.gridOptions.api.onFilterChanged();
    } else if (this.searchValue == "" && this.quickFilterAggregation) {
      this.supportsAggregation = this.quickFilterAggregation;
      this.quickFilterAggregation = false;
      this.gridOptions.api.onFilterChanged();
    }
    super.updateSearch();
  }

  protected accumulateBatchUpdate(update: {
    transaction: RowDataTransaction;
    callback?: (res: RowNodeTransaction) => void;
  }): void {
    if (!this.areUpdatesFrozen() && this?.gridOptions?.api != null) {
      for (let data of update.transaction.add) {
        const id = `${data.orderId}`;
        if (this.pendingBatchTransaction.remove.has(id)) {
          this.pendingBatchTransaction.remove.delete(id);
        }

        this.pendingBatchTransaction.add.set(id, data);
      }

      for (let data of update.transaction.update) {
        const id = `${data.orderId}`;
        if (this.pendingBatchTransaction.add.has(id)) {
          this.pendingBatchTransaction.add.set(id, data);
        } else {
          this.pendingBatchTransaction.update.set(id, data);
        }
      }

      for (let data of update.transaction.remove) {
        const id = `${data.orderId}`;

        if (this.pendingBatchTransaction.update.has(id)) {
          this.pendingBatchTransaction.update.delete(id);
        }

        if (this.pendingBatchTransaction.add.has(id)) {
          this.pendingBatchTransaction.add.delete(id);
        } else {
          this.pendingBatchTransaction.remove.set(id, data);
        }
      }

      if (update.callback != null && typeof update.callback === "function") {
        this.pendingBatchTransaction.callbacks.add(update.callback);
      }
    }
  }

  protected doBatchUpdate() {
    const transaction: RowDataTransaction = {
      add: [...this.pendingBatchTransaction.add.values()],
      update: [...this.pendingBatchTransaction.update.values()],
      remove: [...this.pendingBatchTransaction.remove.values()],
    };
    const callbacks = [...this.pendingBatchTransaction.callbacks];

    this.pendingBatchTransaction.add.clear();
    this.pendingBatchTransaction.update.clear();
    this.pendingBatchTransaction.remove.clear();
    this.pendingBatchTransaction.callbacks.clear();

    transaction.add.forEach((value) => {
      if (value.isLadderRate || value.fullWidth) {
        return;
      }
      this.updateTemperature(value);
    });

    transaction.remove.forEach((aggregate) => {
      this.aggregateExpanded.delete(aggregate.aggregateId);
    });

    transaction.update
      .filter((value) => {
        if (value.isLadderRate || value.fullWidth) {
          return true;
        }
        this.updateTemperature(value);
        return value.isLadderAggregate === true && value.aggregateExpanded === false;
      })
      .map((aggregate) => {
        if (!aggregate.aggregateExpanded) {
          this.aggregateExpanded.delete(aggregate.aggregateId);
        }
      });

    const res = this.gridOptions.api.updateRowData(transaction);

    for (let callback of callbacks) {
      callback(res);
    }
  }

  public onSetColumnVisible(colDef: BfxColDef): void {
    super.onSetColumnVisible(colDef);

    if (colDef.colId === COLUMN_LADDER_POSITION) {
      this.gridOptions?.api?.onSortChanged?.();
    }
  }

  public onSelectionChanged(selected?: RowNode[]): void {
    selected = selected ?? this.gridOptions?.api?.getSelectedNodes();

    super.onSelectionChanged(selected);
  }

  protected onRowSelected(event, selectedNodes?: RowNode[]) {
    if (
      LadderAggregate.is(event.node.data) &&
      this.aggregateExpanded.has(event.node.data.aggregateId) &&
      !this._aggregateRowDeselectInProgress &&
      !this.isDoingDragSelect
    ) {
      this._aggregateRowDeselectInProgress = true;
      selectedNodes = this.selectAllAggregatedRow(event, selectedNodes);
      this._aggregateRowDeselectInProgress = false;
    } else if (
      this.aggregateExpanded.has(event.node.data.aggregateId) &&
      !event.node.isSelected() &&
      !this.isDoingDragSelect
    ) {
      let aggregateNode = this.gridOptions?.api?.getRowNode(event.node.data.aggregateId);

      if (aggregateNode.isSelected()) {
        this._aggregateRowDeselectInProgress = true;
        aggregateNode.setSelected(false, false);
        super.onRowSelected(event, selectedNodes);
        return;
      }
    }

    super.onRowSelected(event, selectedNodes);
    this._aggregateRowDeselectInProgress = false;
  }

  private selectAllAggregatedRow(event: any, selectedNodes: RowNode[]) {
    const isSelected = event.node.isSelected();
    let selectionSet: Set<string> = event.node.data.aggregateOrderIdSet;

    if (isSelected) {
      selectedNodes = selectedNodes ?? this.gridOptions.api.getSelectedNodes();
      let selectedSet = new Set(selectedNodes.map((node) => node.id));

      selectionSet = event.node.data.aggregateOrderIdSet.difference(
        selectedSet.intersection<string>(event.node.data.aggregateOrderIdSet),
      );
    }
    selectionSet.forEach((id) =>
      this.gridOptions?.api?.getRowNode(id)?.setSelected?.(isSelected, false),
    );
    return selectedNodes;
  }

  public hasPendingFinishAction(node: RowNode): boolean {
    return LadderAggregate.is(node.data) && this.aggregateExpanded.has(node.data.aggregateId);
  }

  public ladderPairTrackBy(index: number, item: LadderPair): string {
    return item.toString();
  }

  public updateTemperature(data) {
    if (data.isLadderRate || data.fullWidth) {
      return;
    }

    this.service.tableDefinitionFactory.updateTemperature(data, this.gridOptions.context);
  }
}
